<script setup lang="ts">
import { useHouseSession } from "@/features/dream-home/store/houseSession";
import { useGlobalUser } from "@/stores/globalUser";
import { useMate } from "@/stores/mate";
import { showLoadingToast } from "vant";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { HouseScene } from "../scenes/HouseScene";
import HouseChatList from "./HouseChatList.vue";
import InteractionEventList from "@/components/interaction-review/InteractionEventList.vue";
import { useHouseScene } from "../store/houseScene";
import { useRouter } from "vue-router";
import { useWindowSize } from "@vueuse/core";

const props = defineProps<{
  show: boolean;
  mateId: number;
  spriteId: number;
  scene?: HouseScene;
}>();

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
}>();

const houseSessionStore = useHouseSession();
const houseSceneStore = useHouseScene();
const mateStore = useMate();
const userStore = useGlobalUser();
const router = useRouter();
const { t } = useI18n();
// const interactStore = useInteractive();

const activeTab = ref<"thoughts" | "interaction">("thoughts");
const targetSprite = computed(() => {
  return houseSessionStore.houseSession?.mateSprites.find(
    (sprite) => sprite.id === props.spriteId
  );
});
const targetMate = computed(() => mateStore.getMyMateById(props.mateId));
const isThinking = computed(() => {
  return props.scene?.charMap?.[props.spriteId]?.bulb.visible ?? false;
});
const newRecord = computed(() => {
  return houseSessionStore.mateId2MessageList[props.mateId]?.[0];
});
// const isInPool = computed(() => {
//   return interactStore.mateId2PoolMap.has(props.mateId);
// });

const changeActive = async (enable: boolean) => {
  if (enable) {
    const pass = await houseSceneStore.checkPowerAmountForThought();
    if (!pass) {
      return;
    }
  }
  const loading = showLoadingToast(t("Message.saving"));
  try {
    await mateStore.updateMateDetail(targetMate.value!.uuid, {
      isHouseEnabled: enable,
    });
  } finally {
    loading.close();
  }
};
// const changeInteraction = async (enable: boolean) => {
//   const loading = showLoadingToast(t("Message.saving"));
//   try {
//     if (enable) {
//       await interactStore.addToPool(props.mateId, props.spriteId);
//     } else {
//       await interactStore.removeFromPool(
//         interactStore.mateId2PoolMap.get(props.mateId)?.id ?? 0
//       );
//     }
//   } finally {
//     loading.close();
//   }
// };

const { height: windowHeight } = useWindowSize();
</script>
<template>
  <VanPopup
    :show="props.show"
    @update:show="emit('update:show', $event)"
    class="w-full grid grid-rows-[auto_auto_1fr] bg-base-300 overflow-hidden"
    :style="{ height: `${windowHeight * 0.5 + 38}px` }"
    position="bottom"
    overlay-class="hidden"
  >
    <div class="flex justify-between p-2">
      <div class="flex items-center gap-2">
        <div class="h-16 aspect-[3/4] rounded border-2 overflow-hidden">
          <img
            :src="targetSprite?.imageMedia?.url"
            class="h-full w-full object-contain"
          />
        </div>
        <div class="flex flex-col justify-between">
          <div class="">{{ targetSprite?.mate?.name }}</div>
          <div class="flex flex-col gap-0.5">
            <div class="text-xs">
              {{ $t("house.status") }}:
              {{ isThinking ? $t("house.thinking") : $t("house.strolling") }}
            </div>
            <div v-if="mateId" class="text-xs flex items-center gap-2">
              <div
                :class="{
                  'line-through decoration-2 decoration-error':
                    !targetMate!.isHouseEnabled,
                }"
              >
                💬
                {{ $t("house.thought") }}:
              </div>
              <VanSwitch
                :model-value="targetMate!.isHouseEnabled"
                size="small"
                @change="changeActive($event)"
              />
            </div>
            <div class="flex items-center gap-1 text-xs w-full">
              {{ $t("house.activeToThinking") }}

              <template v-if="!userStore.isFreeModel()">
                ,{{ $t("house.cost") }}

                <PowerIcon class="icon-xs" />
                {{ userStore.getPowerCostByModel(1) }}
              </template>
            </div>
            <div class="flex items-center justify-between gap-1 text-xs">
              <button
                class="btn btn-xs rounded"
                @click="
                  router.push({
                    name: 'mateMemory',
                    query: { mateUuid: targetMate?.uuid },
                  })
                "
              >
                <i class="iconfont icon-memory font-normal" />
                {{ $t(`User.mate.module.memory`) }}
              </button>

              <SelectModelBtn tips="left" />
            </div>
          </div>
        </div>
      </div>
      <button
        class="btn btn-sm btn-ghost btn-square"
        @click="emit('update:show', false)"
      >
        <i class="iconify mingcute--close-line" />
      </button>
    </div>
    <VanTabs v-model:active="activeTab" shrink>
      <VanTab :title="$t('house.thought')" name="thoughts" />
      <VanTab :title="$t('house.interaction')" name="interaction" />
    </VanTabs>
    <div v-if="activeTab === 'thoughts'" class="w-full h-full overflow-hidden">
      <HouseChatList
        v-if="mateId"
        :key="mateId"
        :mate-id="mateId"
        :is-posting-msg="false"
        :show-avatar="false"
        class="px-2 mt-2"
        hide-newest
      >
        <template #header>
          <div class="flex flex-col gap-2">
            <RedotTitle
              :title="$t('house.newestThought')"
              class="text-xs ml-3"
            />
            <div class="bg-base-100 text-xs rounded p-2">
              {{ newRecord?.content || $t("house.noNewThoughts") }}
            </div>
          </div>
          <RedotTitle
            :title="$t('house.recentThoughts')"
            class="text-xs ml-3 mt-2"
          />
        </template>
      </HouseChatList>
      <div v-else class="flex flex-col items-center justify-center">
        <div class="loading loading-sm"></div>
      </div>
    </div>
    <div
      v-if="activeTab === 'interaction'"
      class="w-full h-full overflow-hidden"
    >
      <InteractionEventList
        class="w-full h-full overflow-y-scroll"
        :mate-id="targetSprite?.mate?.id"
        :enable-filter-mate="false"
      />
    </div>
  </VanPopup>
</template>
